<template>
  <div class="card">
    <PageHeader
        :showBtn="false"
        title="Client Wise Statement Summary "
        subtitle="Bills tagged with a client and dated in the range, with what was paid and discounted on them up to the To date."
    >
      <template #extra-btn>
        <div class="d-flex align-items-center gap-1">
          <button class="btn btn-sm btn-outline-primary" @click="goToPrint">
            <i class="fas fa-print"></i> Print
          </button>
          <button class="btn btn-sm btn-outline-success" @click="exportReport">
            <i class="fas fa-file-excel"></i> Export
          </button>
        </div>
      </template>
    </PageHeader>

    <div class="px-2 mt-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-auto d-flex align-items-end mt-2">
          <FilterPopover
              ref="filterPopoverRef"
              title="Filters"
              :active-count="activeFilterCount"
              @open="onOpenFilter"
              @apply="onApplyFilters"
              @clear="onClearFilters"
          >
            <div class="col-12 col-sm-6">
              <label class="filter-label">Client</label>
              <AsyncSelect
                  placeholder="Select Client"
                  v-model="draftFilters.client"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ type: 'client' }"
                  :format-label="formatPatientLabel"
                  :reduce="option => option"
              />
            </div>
            <div class="col-12 col-sm-6">
              <label class="filter-label">Referrer</label>
              <AsyncSelect
                  placeholder="Select Referrer"
                  v-model="draftFilters.referrer"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ type: 'agent' }"
                  :format-label="formatPatientLabel"
                  :reduce="option => option"
              />
            </div>
            <div class="col-12 col-sm-6">
              <label class="filter-label">Consultant / Doctor</label>
              <AsyncSelect
                  placeholder="Select Consultant"
                  v-model="draftFilters.doctor"
                  :api-service="fetchServiceList"
                  :additional-query="{ resource_type: 'human_resources' }"
                  :format-label="formatPatientLabel"
                  :reduce="option => option"
              />
            </div>
            <div class="col-12 col-sm-6">
              <label class="filter-label">Patient</label>
              <AsyncSelect
                  placeholder="Patient Name, ID, Mobile No"
                  v-model="draftFilters.patient"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ type: 'patient' }"
                  :format-label="formatPatientLabel"
                  :reduce="option => option"
              />
            </div>
          </FilterPopover>
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-2 mt-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover">
          <thead>
          <tr>
            <th>SL</th>
            <th>Client</th>
            <th>Mobile No</th>
            <th v-for="column in amountColumns" :key="column.key" class="text-end">{{ column.label }}</th>
            <th class="text-center">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in tableItems" :key="getRowKey(item)">
            <td>{{ getSerial(index) }}</td>
            <td>{{ formatNameWithSerial(item.client_name, item.serial_no) }}</td>
            <td>{{ item.mobile_no }}</td>
            <td
                v-for="column in amountColumns"
                :key="column.key"
                class="text-end text-nowrap"
                :class="{ 'fw-bold due-amount': column.key === 'due_amount' }"
            >
              {{ commaFormat(item[column.key]) }}
            </td>
            <td class="text-center text-nowrap">
              <div class="d-inline-flex align-items-center gap-1">
                <button type="button" class="btn btn-sm btn-outline-primary" title="Print Due Report" @click="printClientDueReport(item)">
                  <i class="fas fa-print"></i>
                </button>
                <button v-if="canOpenReceipt(item)" type="button" class="btn btn-sm btn-primary" title="View Due Bills" @click="openClientReceipt(item)">
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </td>
          </tr>
          </tbody>
          <tfoot v-if="hasItems">
          <tr class="fw-bold">
            <td colspan="3" class="text-end">Total</td>
            <td v-for="column in amountColumns" :key="column.key" class="text-end text-nowrap">
              {{ commaFormat(totals[column.key]) }}
            </td>
            <td></td>
          </tr>
          </tfoot>
        </table>
        <p v-if="!hasItems" class="text-center mt-5">{{ emptyText }}</p>
      </div>
    </div>

    <div class="px-2 mb-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import PageHeader from '@/components/atom/PageHeader.vue'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import Pagination from '@/components/atom/Pagination.vue'
import Loader from '@/components/atom/LoaderComponent.vue'
import handlePurchaseAndSales from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import handleHospital from '@/services/modules/hospital'
import handleCompany from '@/services/modules/company'
import figureFormatter from '@/services/utils/figureFormatter'
import clientSalesStatementSummaryPdf from '@/services/utils/pdf/clientSalesStatementSummaryPdf'
import pendingMoneyReceiptPdf from '@/services/utils/pdf/pendingMoneyReceipt'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import { formatNameWithSerial, resetPaginationPage } from '@/services/utils/global.js'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()

const showError = inject('showError')

const { fetchClientSalesStatementSummary, fetchClientSalesStatementDueBills } = handlePurchaseAndSales()
const { fetchContactProfiles } = handleContact()
const { fetchServiceList } = handleHospital()
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany()
const { commaFormat } = figureFormatter()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { exportToPDF } = clientSalesStatementSummaryPdf()
const { exportToPDF: exportDueReportPdf } = pendingMoneyReceiptPdf()

const amountColumns = [
  { key: 'invoice_amount', label: 'Invoice Amount' },
  { key: 'discount_amount', label: 'Discount Amount' },
  { key: 'net_bill_amount', label: 'Net Bill' },
  { key: 'paid_amount', label: 'Paid Amount' },
  { key: 'due_amount', label: 'Due Amount' }
]

const loading = ref(false)
const hasLoaded = ref(false)
const offset = ref(20)
const tableItems = ref([])
const totals = ref(getEmptyTotals())
const pageMeta = ref({ current_page: 1, per_page: 20 })
const companyInfo = ref({})
const filterPopoverRef = ref(null)
const dueReportCustomization = ref(null)

function getEmptyTotals() {
  return Object.fromEntries(amountColumns.map(column => [column.key, 0]))
}

const getEmptyFilters = () => {
  return {
    client: null,
    referrer: null,
    doctor: null,
    patient: null
  }
}

const copyFilters = (filters) => {
  return Object.assign({}, filters)
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const hasItems = computed(() => tableItems.value.length > 0)

const emptyText = computed(() => {
  if (! hasLoaded.value) return 'Select a date range and click Go'
  return 'No data found'
})

const activeFilterCount = computed(() => {
  return Object.values(appliedFilters.value).filter(value => !! value).length
})

const getRowKey = (item) => {
  return item.contact_profile_client_id
}

const canOpenReceipt = (item) => {
  return Number(item.due_amount) > 0
}

const openClientReceipt = (item) => {
  const query = Object.assign({}, $route.query, {
    client_id: item.contact_profile_client_id,
    client_name: item.client_name,
    client_serial_no: item.serial_no || '',
    client_mobile_no: item.mobile_no || '',
    redirect_route: 'client-sales-statement-summary'
  })
  $router.push({ name: 'pending-money-receipt-clientwise', params: $route.params, query })
}

const getSerial = (index) => {
  return (pageMeta.value.current_page - 1) * pageMeta.value.per_page + index + 1
}

const hasDateRange = () => {
  return !! ($route.query.start && $route.query.end)
}

const buildFilterLines = () => {
  const filters = appliedFilters.value
  const lines = []
  if (filters.client) lines.push('Client: ' + filters.client.name)
  if (filters.referrer) lines.push('Referrer: ' + filters.referrer.name)
  if (filters.doctor) lines.push('Doctor: ' + filters.doctor.name)
  if (filters.patient) lines.push('Patient: ' + filters.patient.name)
  return lines
}

const buildFilterQuery = () => {
  const filters = appliedFilters.value
  let query = '?company_id=' + $route.params.companyId
  query += '&start_date=' + $route.query.start
  query += '&end_date=' + $route.query.end
  if (filters.client) query += '&contact_profile_client_id=' + filters.client.id
  if (filters.referrer) query += '&referrer_id=' + filters.referrer.id
  if (filters.doctor) query += '&service_resource_id=' + filters.doctor.id
  if (filters.patient) query += '&contact_profile_id=' + filters.patient.id
  return query
}

const buildPageQuery = (page) => {
  return buildFilterQuery() + '&offset=' + offset.value + '&page=' + page
}

const resetTable = () => {
  tableItems.value = []
  totals.value = getEmptyTotals()
  $store.commit('resetPagination')
}

const setStatement = (res) => {
  tableItems.value = res.data.data
  totals.value = res.totals
  pageMeta.value = { current_page: res.data.current_page, per_page: res.data.per_page }
  $store.commit('setPaginationData', {
    records: res.data.total,
    perPage: res.data.per_page,
    currentPage: res.data.current_page
  })
}

const loadData = async (page) => {
  if (! hasDateRange()) {
    showError('Please select the From and To date')
    return
  }
  loading.value = true
  try {
    const res = await fetchClientSalesStatementSummary(buildPageQuery(page))
    hasLoaded.value = true
    if (! res.status) {
      showError(res.message)
      resetTable()
      return
    }
    setStatement(res)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const loadFirstPage = async () => {
  resetPaginationPage($store, $route, $router)
  await loadData(1)
}

const onClickGo = async () => {
  await loadFirstPage()
}

const onOpenFilter = () => {
  draftFilters.value = copyFilters(appliedFilters.value)
}

const onApplyFilters = async () => {
  appliedFilters.value = copyFilters(draftFilters.value)
  await loadFirstPage()
}

const onClearFilters = async () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  if (filterPopoverRef.value) filterPopoverRef.value.close()
  await loadFirstPage()
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({ path: $route.path, query: routeQuery })
  await loadData(page)
}

const goToPrint = async () => {
  if (! hasDateRange()) {
    showError('Please select the From and To date')
    return
  }
  loading.value = true
  try {
    const res = await fetchClientSalesStatementSummary(buildFilterQuery() + '&is_all=1')
    if (! res.status) {
      showError(res.message)
      return
    }
    exportToPDF(companyInfo.value, res, $route.query.start, $route.query.end, buildFilterLines())
  } catch (err) {
    showError('Failed to generate PDF')
  } finally {
    loading.value = false
  }
}

const buildClientBillQuery = (item) => {
  return buildFilterQuery() + '&contact_profile_client_id=' + item.contact_profile_client_id
}

const toDueReportRow = (bill) => {
  const invoiceAmount = Number(bill.invoice_amount)
  const paidAmount = Number(bill.paid_amount)
  const dueAmount = Number(bill.due_amount)
  return {
    bill_number: bill.bill_number,
    date: bill.date,
    contact_name: bill.contact_name,
    contact_profile_id: bill.contact_profile_id,
    item_details: bill.item_details,
    total_amount: invoiceAmount,
    points_redeemed: 0,
    discount: Number(bill.discount_amount),
    paid_amount: paidAmount,
    adjusted_amount: (invoiceAmount - paidAmount - dueAmount).toFixed(2)
  }
}

const formatTotal = (value) => {
  return String(commaFormat(value))
}

const toDueReportTotal = (reportTotals) => {
  return {
    total_amount_with_points: formatTotal(reportTotals.invoice_amount),
    total_discount_amount: formatTotal(reportTotals.discount_amount),
    total_paid_amount: formatTotal(reportTotals.paid_amount),
    total_due_amount: formatTotal(reportTotals.due_amount)
  }
}

const buildDueReportPayload = (item) => {
  const patient = appliedFilters.value.patient
  return {
    billType: null,
    orderType: null,
    businessUnit: null,
    customerName: patient ? patient.name : null,
    clientName: formatNameWithSerial(item.client_name, item.serial_no),
    paymentStatus: 'DUE'
  }
}

const fetchDueReportCustomization = async () => {
  if (dueReportCustomization.value) return dueReportCustomization.value
  const query = '?company_id=' + $route.params.companyId + '&customization_type=pdf&type=pending_money_receipt_pdf'
  const res = await fetchActiveCustomizationField(query).catch(() => ({}))
  if (! res.status) return {}
  dueReportCustomization.value = res.data
  return res.data
}

const printClientDueReport = async (item) => {
  if (! hasDateRange()) {
    showError('Please select the From and To date')
    return
  }
  loading.value = true
  try {
    const [res, customizationFields] = await Promise.all([
      fetchClientSalesStatementDueBills(buildClientBillQuery(item)),
      fetchDueReportCustomization()
    ])
    if (! res.status) {
      showError(res.message)
      return
    }
    const date = { startDate: $route.query.start, endDate: $route.query.end }
    exportDueReportPdf(companyInfo.value, res.data.map(toDueReportRow), toDueReportTotal(res.totals), date, buildDueReportPayload(item), customizationFields)
  } catch (err) {
    showError('Failed to generate PDF')
  } finally {
    loading.value = false
  }
}

const exportReport = () => {
  if (! hasDateRange()) {
    showError('Please select the From and To date')
    return
  }
  const token = localStorage.getItem('token')
  const query = buildFilterQuery() + '&is_export=1&_token=' + token
  const url = `${process.env.VUE_APP_BASE_URL}/export/inventory/client-sales-statement-summary${query}`
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.click()
}

const getCompanyInfo = async () => {
  try {
    const res = await fetchCompanyInfo($route.params.companyId)
    if (! res.status) return
    companyInfo.value = res.data
  } catch (err) {
    showError('Something went wrong')
  }
}

const getRoutePage = () => {
  return Number($route.query.page) || 1
}

const loadInitialData = () => {
  if (! hasDateRange()) return
  loadData(getRoutePage())
}

onMounted(() => {
  $store.commit('resetPagination')
  getCompanyInfo()
  loadInitialData()
})
</script>

<style scoped>
.due-amount {
  font-size: 1.2rem;
  font-weight: 700;
}
</style>
